import {
  getBusinessHours,
  updateBusinessHours,
} from '@/services/business-hours/business-hours.service';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BusinessHoursPayload } from '@/services/business-hours/business-hours.types';
import { openNotification } from '@/utils/notification/notification';

export const useBusinessHours = (company_id: string | undefined, date?: string) => {
  const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data: businessHoursData, isLoading } = useQuery({
    queryKey: ReactQueryKeys.BUSINESS_HOURS(company_id, date),
    queryFn: () => getBusinessHours({ company_id: company_id!, time_zone, date: date! }),
    enabled: !!company_id && !!date,
  });

  return { businessHoursData, isLoading };
};

export const useUpdateBusinessHours = (company_id: string | undefined) => {
  const queryClient = useQueryClient();
  const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    mutate: updateCompanyBusinessHours,
    mutateAsync: updateCompanyBusinessHoursAsync,
    isPending: isPendingUpdateBusinessHours,
  } = useMutation({
    mutationFn: (payload: BusinessHoursPayload) =>
      updateBusinessHours(payload, { company_id: company_id!, time_zone }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['business-hours'],
      });
      openNotification({
        message: 'Business hours updated successfully.',
        type: 'success',
      });
    },
    onError: () => {
      openNotification({
        message: 'Failed to update business hours.',
        type: 'error',
      });
    },
  });

  return {
    updateCompanyBusinessHours,
    updateCompanyBusinessHoursAsync,
    isPendingUpdateBusinessHours,
  };
};
